import type {
  PayrollStatementNotificationRecipient,
  PayrollStatementSentPackState,
} from "@/payroll/pay-breakdown/payroll-statement-notifications/types";
import { getNotificationReasonLabel } from "./labels";

export const getRecipientsWithChangedPacks = (
  recipients: PayrollStatementNotificationRecipient[],
) =>
  recipients.filter(
    (recipient) => recipient.sent_pack?.changed === true && recipient.eligible,
  );

export const getBlockedChangedPackRecipients = (
  recipients: PayrollStatementNotificationRecipient[],
) =>
  recipients.filter(
    (recipient) => recipient.sent_pack?.changed === true && !recipient.eligible,
  );

export const hasSentPackDrift = ({
  recipients,
  states,
}: {
  recipients: PayrollStatementNotificationRecipient[];
  states?: PayrollStatementSentPackState[];
}) => {
  if (!states) return false;

  const held = new Map(
    recipients.map((recipient) => [
      recipient.technician_id,
      recipient.sent_pack,
    ]),
  );

  return states.some((state) => {
    if (!held.has(state.technician_id)) return false;

    const sentPack = held.get(state.technician_id);
    return (
      sentPack?.send_count !== state.send_count ||
      sentPack?.last_sent_at !== state.last_sent_at
    );
  });
};

const NAMED_LIMIT = 6;

const summarizeNames = (
  recipients: PayrollStatementNotificationRecipient[],
) => {
  const names = recipients.map((recipient) => recipient.name);
  if (names.length <= NAMED_LIMIT) return names.join(", ");

  return `${names.slice(0, NAMED_LIMIT).join(", ")} and ${names.length - NAMED_LIMIT} more`;
};

const sharedBlockedReason = (
  recipients: PayrollStatementNotificationRecipient[],
) => {
  const reasons = recipients.map(
    (recipient) => recipient.ineligible_reason ?? "",
  );
  const first = reasons[0];
  if (!first || reasons.some((reason) => reason !== first)) return null;

  return getNotificationReasonLabel(first)?.toLowerCase() ?? null;
};

export const getChangedPacksSummary = ({
  changedRecipients,
  blockedRecipients,
}: {
  changedRecipients: PayrollStatementNotificationRecipient[];
  blockedRecipients: PayrollStatementNotificationRecipient[];
}) => {
  const sendable = changedRecipients.length;
  const blocked = blockedRecipients.length;
  const named = sendable > 0 ? changedRecipients : blockedRecipients;
  if (named.length === 0) return { headline: null, detail: null };

  const headline =
    named.length === 1
      ? `${named[0].name}'s pay changed after their PayPack was published`
      : `${named.length} technicians' pay changed after their PayPacks were published`;

  const reason = blocked > 0 ? sharedBlockedReason(blockedRecipients) : null;
  const suffix = reason ? `: ${reason}.` : ".";
  const blockedClause =
    blocked === 0
      ? null
      : sendable === 0
        ? `${summarizeNames(blockedRecipients)} can't be emailed${suffix}`
        : `${summarizeNames(blockedRecipients)} ${blocked === 1 ? "also needs" : "also need"} a fresh PayPack, but can't be emailed${suffix}`;

  const detail =
    [
      sendable > 1 ? `${summarizeNames(changedRecipients)}.` : null,
      blockedClause,
    ]
      .filter(Boolean)
      .join(" ") || null;

  return { headline, detail };
};
